import { type PaletteKey, type Pixel, type PixelCanvas, sphere, sprite } from '../../lib';

// The core combat effects at 4× density (ADR-0111 follow-up): the same world sizes and pivots as the 1× art, drawn with
// four art px per world px. The helpers below are shared with the other small bits (juice, war fx, loot, cards).

type Pt = readonly [number, number];

/** Light from the top-left, in the picture plane. */
const LIGHT: Pt = [-0.6, -0.8];

/**
 * A thick polyline through `pts` (art px), `w` across, its ends narrowed over `taper` art px (0: square ends).
 * `tone(along, side)` colours each pixel by where it is along the line (0..1) and across it (1 on the side towards
 * the top-left light, -1 on the far side); null leaves it.
 */
export function strand(
  c: PixelCanvas,
  pts: readonly Pt[],
  w: number,
  tone: (along: number, side: number) => Pixel,
  taper = 0,
): PixelCanvas {
  const lens = pts.slice(1).map((p, i) => Math.hypot(p[0] - pts[i]![0], p[1] - pts[i]![1]));
  const total = lens.reduce((a, b) => a + b, 0);
  const xs = pts.map((p) => p[0]);
  const ys = pts.map((p) => p[1]);
  for (let y = Math.floor(Math.min(...ys) - w); y <= Math.ceil(Math.max(...ys) + w); y++) {
    for (let x = Math.floor(Math.min(...xs) - w); x <= Math.ceil(Math.max(...xs) + w); x++) {
      const [px, py] = [x + 0.5, y + 0.5];
      let best = Number.POSITIVE_INFINITY;
      let along = 0;
      let side = 0;
      let acc = 0;
      for (let i = 0; i < lens.length; i++) {
        const [ax, ay] = pts[i]!;
        const [bx, by] = pts[i + 1]!;
        const len = lens[i]!;
        const [dx, dy] = [(bx - ax) / len, (by - ay) / len];
        const t = Math.max(0, Math.min(len, (px - ax) * dx + (py - ay) * dy));
        const [qx, qy] = [ax + dx * t, ay + dy * t];
        const d = Math.hypot(px - qx, py - qy);
        if (d < best) {
          best = d;
          along = (acc + t) / total;
          // The normal turned towards the light.
          const flip = -dy * LIGHT[0] + dx * LIGHT[1] < 0 ? -1 : 1;
          side = ((px - qx) * -dy + (py - qy) * dx) * flip;
        }
        acc += len;
      }
      const end = Math.min(along * total, (1 - along) * total) + 0.5;
      const hw = (w / 2) * (taper > 0 ? Math.min(1, 0.35 + (0.65 * end) / taper) : 1);
      if (best > hw) continue;
      const col = tone(along, side / Math.max(hw, 0.5));
      if (col !== null) c.set(x, y, col);
    }
  }
  return c;
}

/** Whether the pixel centre (x + .5, y + .5) is inside the polygon. */
function inPolygon(pts: readonly Pt[], x: number, y: number): boolean {
  const [px, py] = [x + 0.5, y + 0.5];
  let inside = false;
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    const [xi, yi] = pts[i]!;
    const [xj, yj] = pts[j]!;
    if (yi > py !== yj > py && px < ((xj - xi) * (py - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

/**
 * A hard little chunk (a chip of stone, a splinter, a shard): the polygon filled `mid`, its rim facing the top-left
 * light `lit` and the rim facing away `dark`, 1 art px each.
 */
export function chunk(
  c: PixelCanvas,
  pts: readonly Pt[],
  [lit, mid, dark]: readonly [Pixel, Pixel, Pixel],
): PixelCanvas {
  const xs = pts.map((p) => p[0]);
  const ys = pts.map((p) => p[1]);
  const [x0, x1, y0, y1] = [
    Math.floor(Math.min(...xs)),
    Math.ceil(Math.max(...xs)),
    Math.floor(Math.min(...ys)),
    Math.ceil(Math.max(...ys)),
  ];
  const at = (x: number, y: number) => inPolygon(pts, x, y);
  for (let y = y0; y <= y1; y++) {
    for (let x = x0; x <= x1; x++) {
      if (!at(x, y)) continue;
      const toLight = Number(!at(x, y - 1)) + Number(!at(x - 1, y));
      const away = Number(!at(x, y + 1)) + Number(!at(x + 1, y));
      c.set(x, y, toLight > away ? lit : away > toLight ? dark : mid);
    }
  }
  return c;
}

/** Fills every pixel whose centre gets a tone from `f` (heat fields, stars, rings). */
export function field(c: PixelCanvas, f: (x: number, y: number) => Pixel): PixelCanvas {
  for (let y = 0; y < c.h; y++)
    for (let x = 0; x < c.w; x++) {
      const col = f(x + 0.5, y + 0.5);
      if (col !== null) c.set(x, y, col);
    }
  return c;
}

/** Heat in a tapered lobe from a (radius ra) to b (radius rb): 1 on its axis, 0 at its edge, below 0 outside. */
export function lobe(x: number, y: number, [ax, ay, ra]: readonly number[], [bx, by, rb]: readonly number[]): number {
  const [dx, dy] = [bx! - ax!, by! - ay!];
  const t = Math.max(0, Math.min(1, ((x - ax!) * dx + (y - ay!) * dy) / (dx * dx + dy * dy)));
  return 1 - Math.hypot(x - ax! - dx * t, y - ay! - dy * t) / (ra! + (rb! - ra!) * t);
}

/**
 * Fire by heat: a w core, y, Y, then a rim lit `o` towards the light and rust `u` away from it. `cx, cy` is where the
 * light/shade split runs through.
 */
function fire(h: number, x: number, y: number, cx: number, cy: number): Pixel {
  const shade = ((x - cx) * 0.35 + (y - cy)) / 10;
  const k = h - Math.max(0, shade) * 0.12;
  if (h <= 0) return null;
  if (k > 0.66) return 'w';
  if (k > 0.42) return 'y';
  if (k > 0.2) return 'Y';
  return shade > 0.05 ? 'u' : 'o';
}

/** The flash's centre line (art px): the 1× core row 5's middle. */
const AXIS = 22;

type Lobe = readonly [a: readonly number[], b: readonly number[], heat: number];

function flame(lobes: readonly Lobe[], cx: number) {
  return (c: PixelCanvas) =>
    field(c, (x, y) => fire(Math.max(...lobes.map(([a, b, k]) => lobe(x, y, a, b) * k)), x, y, cx, AXIS));
}

const muzzle = sprite({
  name: 'muzzle_flash',
  group: 'fx',
  size: [48, 44],
  origin: [4, 20],
  density: 4,
  notes: [
    'Soft flame teardrop with a forward spike and two petals, w core, y/Y heat, orange-lit rim, rust underside.',
    'Points right from the muzzle (origin at left-middle); 3 frames big/bright → small → smoke wisp with an ember.',
    '"Storybook" direction, picked in the 2026-09 art review. No outline (fire), bright colours only.',
    '4× density (ADR-0111 follow-up): 48×44 art px; tapered lobes on a heat field, so the bands follow the curves.',
    'Rubric (4×): S5 R5 P5 L4 C4 A4 F4',
    'Weakest: the heat bands are concentric (by design for fire), so the petals read a little like a flower at 8×.',
  ].join('\n'),
  anims: {
    flash: {
      fps: 30,
      loop: false,
      frames: [
        flame(
          [
            [[2, AXIS, 2.5], [19, AXIS, 9.5], 1],
            [[19, AXIS, 9.5], [33, AXIS - 0.3, 5], 0.86],
            [[33, AXIS - 0.3, 5], [47.5, AXIS - 0.5, 1.2], 0.6],
            [[21, AXIS - 5, 5.5], [25.5, 5, 1.8], 0.65],
            [[17, AXIS + 5, 5], [14, 38, 1.8], 0.6],
            [[11, AXIS - 3, 3.2], [7.5, 15, 1.4], 0.6],
          ],
          16,
        ),
        flame(
          [
            [[4, AXIS, 2], [15, AXIS - 0.5, 6.5], 0.82],
            [[15, AXIS - 0.5, 6.5], [31, AXIS - 1, 1.5], 0.82],
            [[34, AXIS - 2.5, 3.2], [42, AXIS - 3.5, 1.2], 0.66],
            [[17, AXIS - 3, 3.5], [21, 11, 1], 0.62],
            [[12, AXIS + 3, 3], [15, 30, 1], 0.55],
          ],
          14,
        ),
        (c: PixelCanvas) => {
          // The smoke the shot leaves: a small cool wisp, lit top-left, and an ember flying on.
          for (const [x, y, r] of [
            [14, 24.5, 3.6],
            [22, 23, 4.2],
            [17.5, 19.5, 4.6],
          ] as const)
            sphere(c, x, y, r, r * 0.9, ['4', '5', 'e'], { bands: [0.1, 0.6] });
          c.set(37, 20, 'Y').set(36, 21, 'Y').set(37, 21, 'y').set(38, 21, 'y').set(37, 22, 'Y').set(39, 21, 'Y');
          c.set(34, 21, 'o').set(33, 21, 'u');
        },
      ],
    },
  },
});

/** The bullet's seed: an egg lit from the top-left, centre (art px) and radii. */
const SEED = { x: 27, y: 10, rx: 8.6, ry: 7.4 } as const;

function bulletFrame(broken: boolean) {
  return (c: PixelCanvas) => {
    // The ember streak behind it: thin and rust at its tail, thicker and hotter towards the seed.
    const streak = (x0: number) =>
      strand(
        c,
        [
          [x0, SEED.y + 0.2],
          [SEED.x - 4, SEED.y],
        ],
        5,
        (along, side) => {
          if (along < 0.3) return 'u';
          if (along < 0.55) return side > -0.2 ? 'o' : 'u';
          if (along < 0.78) return side > 0.5 ? 'y' : side > -0.5 ? 'Y' : 'o';
          return side > -0.6 ? 'y' : 'Y';
        },
        12,
      );
    if (broken) {
      streak(6);
      // A flicker: the tail breaks off into a spark behind.
      c.set(1, 10, 'u').set(2, 10, 'o').set(2, 9, 'u').set(3, 10, 'u');
    } else streak(0);
    // The seed: an egg, a 2-px rim lit U on top, sel-out n on its belly and nose.
    const inEgg = (x: number, y: number, grow: number) => {
      const nx = (x + 0.5 - SEED.x) / (SEED.rx + grow);
      const ny = (y + 0.5 - SEED.y) / (SEED.ry + grow);
      // A seed: a touch pointier at the back.
      return nx * nx + ny * ny * (nx < 0 ? 1 + -nx * 0.35 : 1) <= 1;
    };
    for (let y = 0; y < c.h; y++)
      for (let x = 14; x < c.w; x++) {
        if (!inEgg(x, y, 0)) continue;
        const nx = (x + 0.5 - SEED.x) / SEED.rx;
        const ny = (y + 0.5 - SEED.y) / SEED.ry;
        const inner = inEgg(x, y, -2);
        const lit = -nx * 0.55 - ny * 0.8;
        let col: PaletteKey;
        if (!inner) col = lit > 0.15 ? 'U' : lit > -0.25 && nx < 0 ? 'u' : 'n';
        else {
          const d = Math.hypot(nx + 0.3, ny + 0.38);
          col = d < 0.32 ? 'w' : d < 0.62 ? 'y' : lit > -0.45 ? 'Y' : 'U';
        }
        c.set(x, y, col);
      }
  };
}

const bullet = sprite({
  name: 'bullet',
  group: 'fx',
  size: [36, 20],
  origin: [24, 8],
  density: 4,
  notes: [
    'Warm glowing seed, orange-lit top, brown sel-out belly, an ember streak tapering behind it.',
    'Head right, rotated along velocity; the streak flickers between the 2 frames (whole / its tail broken off).',
    'A glow sprite goes under it.',
    '"Storybook" direction, picked in the 2026-09 art review.',
    '4× density (ADR-0111 follow-up): 36×20 art px, a 2-px rim (U lit, n belly), a w hot spot, a 5-px streak.',
    'Rubric (4×): S4 R5 P5 L5 C4 A4 F4',
    'Weakest: at world ×2 the seed and the streak merge into one warm dash; the seed shape shows only at ×4.',
  ].join('\n'),
  anims: { idle: { fps: 15, frames: [bulletFrame(false), bulletFrame(true)] } },
});

const glow = sprite({
  name: 'glow',
  group: 'fx',
  size: [36, 36],
  origin: [16, 16],
  density: 4,
  notes: `Additive halo (the client sets blend, alpha and a tint, and scales it up to ×12). Concentric steps of the fire
ramp, hottest in the middle (y, Y, o) to a dim rust rim (u), so it fades out instead of ending on a hard orange edge;
no dither (scaled ×12 it would show as a grid). Centred in its frame like the 1× one (the pivot half a world px off).
4× density (ADR-0111 follow-up): 36×36 art px. Rubric (4×): S4 R5 P5 L5 C5 A- F5. Weakest: still four visible rings
when scaled past ×8 at full alpha (it is drawn at 0.2-0.8).`,
  anims: {
    idle: {
      frames: [
        (c: PixelCanvas) =>
          field(c, (x, y) => {
            const r = Math.hypot(x - 18, y - 18);
            return r < 6.6 ? 'y' : r < 11 ? 'Y' : r < 15 ? 'o' : r < 17.8 ? 'u' : null;
          }),
      ],
    },
  },
});

const casing = sprite({
  name: 'casing',
  group: 'fx',
  size: [12, 8],
  origin: [4, 4],
  density: 4,
  notes: `Ejected seed shell: a little brass cylinder lying on its side, lit along its top (e/y), Y body, U and n
underneath, a rim at its base and its open mouth dark at the right. Tiny and bright so it glints on grass.
4× density (ADR-0111 follow-up): 12×8 art px. Rubric (4×): S4 R4 P5 L5 C4 A- F4. Weakest: the open mouth is 2 px,
seen only at ×4.`,
  anims: {
    idle: {
      frames: [
        `
        ............
        ..eeyyyyyY..
        .eyyyyyyyYU.
        .yYYYYYYYUNn
        .YUUUUUUUUNn
        ..nnnnnnnnn.
        ............
        ............
        `,
      ],
    },
  },
});

/** A four-point star (an astroid): `r` its reach, `p` how thin its arms (lower is thinner), tones by heat. */
function star(cx: number, cy: number, r: number, p: number, tone: (h: number, x: number, y: number) => Pixel) {
  return (c: PixelCanvas) =>
    field(c, (x, y) => {
      const s = (Math.abs(x - cx) / r) ** p + (Math.abs(y - cy) / r) ** p;
      return s > 1 ? null : tone(1 - s, x - cx, y - cy);
    });
}

const spark = sprite({
  name: 'spark',
  group: 'fx',
  size: [12, 12],
  origin: [4, 4],
  density: 4,
  notes: `Hit spark: a four-point star (w core, e, y arms) → a small twinkle. Tinted and often drawn additively by the
client, so bright and outline-free. 4× density (ADR-0111 follow-up): 12×12 art px, arms tapering to 1 px.
Rubric (4×): S5 R4 P5 L4 C5 A4 F4. Weakest: the second frame is a 5-px twinkle, gone in a blink.`,
  anims: {
    idle: {
      fps: 20,
      loop: false,
      frames: [
        star(6.5, 6.5, 6, 0.62, (h) => (h > 0.5 ? 'w' : h > 0.3 ? 'e' : 'y')),
        star(6.5, 6.5, 2.6, 0.7, (h) => (h > 0.45 ? 'e' : 'y')),
      ],
    },
  },
});

function puffs(c: PixelCanvas, list: readonly (readonly [number, number, number])[]) {
  for (const [x, y, r] of list) sphere(c, x, y, r, r * 0.92, ['m', 'S', 't', 'e'], { bands: [-0.05, 0.35, 0.78] });
  return c;
}

/** A leaf bit (lit l, g underside) along a short diagonal. */
const leafBit = (c: PixelCanvas, x: number, y: number, dx: number, dy: number) =>
  strand(
    c,
    [
      [x, y],
      [x + dx, y + dy],
    ],
    2.6,
    (_, side) => (side > 0 ? 'l' : 'g'),
    1.5,
  );

const impact = sprite({
  name: 'impact',
  group: 'fx',
  size: [44, 44],
  origin: [20, 20],
  density: 4,
  notes: [
    'Soft hot star; spark dashes fly, a warm dust puff and leaf bits bloom',
    'Wall (hedge) hit: hot star → spark dashes → dust puff with leaf bits → a few settling specks.',
    '"Storybook" direction, picked in the 2026-09 art review. No outline.',
    '4× density (ADR-0111 follow-up): 44×44 art px; the star is an eight-point heat field (w core, y, Y, o lit rim,',
    'u away from the light), the dashes 2 px with hot heads, the puff three lit balls.',
    'Rubric (4×): S5 R4 P5 L4 C4 A4 F4',
    'Weakest: frame 3 is only specks; the leaf bits are 3-4 px and read as green flecks more than leaves.',
  ].join('\n'),
  anims: {
    burst: {
      fps: 24,
      loop: false,
      frames: [
        (c: PixelCanvas) =>
          field(c, (x, y) => {
            const [dx, dy] = [x - 22, y - 22];
            const a = Math.atan2(dy, dx);
            const reach = 6.5 + 14 * Math.abs(Math.cos(2 * a)) ** 12 + 6 * Math.abs(Math.sin(2 * a)) ** 12;
            return fire(1 - Math.hypot(dx, dy) / reach, x, y, 22, 22);
          }),
        (c: PixelCanvas) => {
          // A small hot puff in the middle…
          field(c, (x, y) => {
            const r = Math.hypot(x - 22, (y - 22) * 1.1);
            if (r > 6.2) return null;
            return r < 2.4 ? 'y' : r < 4.2 ? 'Y' : x + y < 44 ? 'e' : 't';
          });
          // …and the sparks flying out of it: 2-px dashes, their heads hot, their tails cooling.
          for (const [a, r0, r1] of [
            [-2.3, 12, 20],
            [-0.85, 12, 19],
            [Math.PI, 13, 20.5],
            [0, 14, 21],
            [2.35, 13, 19],
            [0.8, 12, 18.5],
          ] as const) {
            const low = Math.sin(a) > 0.3;
            strand(
              c,
              [
                [22 + Math.cos(a) * r0, 22 + Math.sin(a) * r0],
                [22 + Math.cos(a) * r1, 22 + Math.sin(a) * r1],
              ],
              2.2,
              (along) => (along > 0.7 ? (low ? 'Y' : 'y') : along > 0.35 ? (low ? 'o' : 'Y') : low ? 'u' : 'o'),
            );
          }
        },
        (c: PixelCanvas) => {
          puffs(c, [
            [16, 23, 5.5],
            [24, 24, 5],
            [19.5, 18, 6],
          ]);
          leafBit(c, 31, 6, 4, -2);
          leafBit(c, 33, 13, 3, 2);
          leafBit(c, 8, 31, -3, 2);
          c.set(5, 3, 'Y').set(6, 3, 'y').set(5, 4, 'Y');
          c.set(3, 22, 'o').set(4, 22, 'o').set(41, 22, 'o').set(42, 23, 'u');
        },
        (c: PixelCanvas) => {
          for (const [x, y, col] of [
            [17, 9, 't'],
            [12, 14, 'S'],
            [26, 14, 'S'],
            [21, 22, 'm'],
          ] as const)
            c.set(x, y, col)
              .set(x + 1, y, col)
              .set(x, y + 1, col === 't' ? 'S' : 'm');
          leafBit(c, 36, 4, 2, 1);
          leafBit(c, 4, 26, 2, -1);
        },
      ],
    },
  },
});

const dust = sprite({
  name: 'dust',
  group: 'fx',
  size: [36, 32],
  origin: [16, 28],
  density: 4,
  notes: [
    'Warm tan dust cluster, cream-lit caps, brown underside; splits into wisps',
    'Footstep / landing / wall-hit puff at the feet (origin bottom-centre): kicks up low, swells, breaks apart.',
    '"Storybook" direction, picked in the 2026-09 art review. No outline.',
    '4× density (ADR-0111 follow-up): 36×32 art px, puffs as lit balls (e caps, t, S, m underside).',
    'Rubric (4×): S4 R4 P5 L5 C4 A4 F4',
    'Weakest: tan on the dirt lane is low contrast; only the cream caps and the m underside separate it there.',
  ].join('\n'),
  anims: {
    puff: {
      fps: 13,
      loop: false,
      frames: [
        (c: PixelCanvas) =>
          puffs(c, [
            [6.5, 28, 3.6],
            [27.5, 27.2, 4.4],
            [11.5, 25.5, 5.6],
            [20.5, 25, 6.4],
          ]),
        (c: PixelCanvas) =>
          puffs(c, [
            [5.5, 25.5, 4.2],
            [29, 21.5, 5.4],
            [10.5, 19.5, 6.6],
            [21, 16.5, 7.6],
            [15.5, 24.5, 6],
            [25, 25.5, 4.6],
          ]),
        (c: PixelCanvas) =>
          puffs(c, [
            [9, 9.5, 4.6],
            [13.5, 13.5, 3.6],
            [26, 14, 3.6],
            [6.5, 22, 3],
            [27.5, 20.5, 2.6],
          ]),
        (c: PixelCanvas) =>
          puffs(c, [
            [9.5, 6.5, 2.6],
            [29.5, 14, 2.6],
            [5.5, 22.5, 2.6],
          ]),
      ],
    },
  },
});

/** A straw strand: lit y edge, Y, U underside; `node` puts a dark n joint across it (a stalk's knot). */
function straw1(pts: readonly Pt[], node?: number) {
  return (c: PixelCanvas) =>
    strand(
      c,
      pts,
      3,
      (along, side) => {
        if (node !== undefined && Math.abs(along - node) < 0.04) return side > 0.35 ? 'Y' : side > -0.35 ? 'U' : 'n';
        return side > 0.35 ? 'y' : side > -0.35 ? 'Y' : 'U';
      },
      2,
    );
}

const straw = sprite({
  name: 'straw',
  group: 'fx',
  size: [12, 12],
  origin: [4, 4],
  density: 4,
  notes: `Straw bits knocked out of scarecrows. Frames are shape variants, not animation: a bent strand, a straight one
with a joint, an upright one, a diagonal one. 3-px strands lit y on top, Y, U underneath, tapered ends.
4× density (ADR-0111 follow-up): 12×12 art px. Rubric (4×): S4 R4 P5 L4 C4 A- F4. Weakest: on the dirt lane the
U underside is all that parts them from the ground.`,
  anims: {
    variants: {
      frames: [
        straw1([
          [0.8, 3],
          [4, 3.4],
          [6.5, 5],
          [8.5, 7],
          [11.2, 8],
        ]),
        straw1(
          [
            [0.5, 6],
            [11.5, 5.5],
          ],
          0.62,
        ),
        straw1([
          [5.5, 0.5],
          [6.5, 6],
          [5.5, 11.5],
        ]),
        straw1(
          [
            [1, 1],
            [11, 11],
          ],
          0.35,
        ),
      ],
    },
  },
});

const mote = sprite({
  name: 'mote',
  group: 'fx',
  size: [8, 8],
  origin: [4, 4],
  density: 4,
  notes: `Ambient pollen / leaf bits drifting over the garden (also the fireflies' cores, drawn additively and tinted, and
frames 2-3 the grass tufts an impact flicks up). Variants: a yellow pollen speck, a cream one, a lying blade, a
diagonal blade. 4× density (ADR-0111 follow-up): 8×8 art px, round specks with a lit dot, 2-px blades lit l, g below.
Rubric (4×): S4 R4 P5 L4 C5 A- F4. Weakest: the specks are 4 px across, close to the old 1-px dot by design.`,
  anims: {
    variants: {
      frames: [
        `
        ........
        ........
        ...yy...
        ..yeyy..
        ..yyyY..
        ...yY...
        ........
        ........
        `,
        `
        ........
        ........
        ...ee...
        ..ewee..
        ..eeet..
        ...et...
        ........
        ........
        `,
        `
        ........
        ........
        .ll.....
        ..llll..
        ...ggllg
        .....gg.
        ........
        ........
        `,
        `
        ........
        .l......
        .ll.....
        ..ll....
        ..gll...
        ...gl...
        ....gg..
        ........
        `,
      ],
    },
  },
});

const glint = sprite({
  name: 'glint',
  group: 'fx',
  size: [20, 20],
  origin: [8, 8],
  density: 4,
  notes: `Water sparkle (and the apple crate's twinkle): a small cyan star with a w core → it opens into a big thin
twinkle (w core, four 1-px arms c fading to b) → a last small c point. Outline-free, bright. 4× density (ADR-0111
follow-up): 20×20 art px. Rubric (4×): S5 R4 P5 L4 C5 A4 F4. Weakest: the thin arms of the middle frame are 1 art px,
faint at world ×2 on the lane.`,
  anims: {
    twinkle: {
      fps: 10,
      loop: false,
      frames: [
        star(10.5, 10.5, 5, 0.6, (h) => (h > 0.5 ? 'w' : 'c')),
        star(10.5, 10.5, 9.5, 0.42, (h, dx, dy) => {
          const r = Math.abs(dx) + Math.abs(dy);
          return r < 2 ? 'w' : r < 5.5 ? (h > 0.15 ? 'w' : 'c') : r < 8 ? 'c' : 'b';
        }),
        star(10.5, 10.5, 2.6, 0.75, (h) => (h > 0.5 ? 'w' : 'c')),
      ],
    },
  },
});

export default [muzzle, bullet, glow, casing, spark, impact, dust, straw, mote, glint];
